import React from 'react';
import { motion } from 'framer-motion';
import { FileTextIcon, TargetIcon, RefreshCwIcon, UserCheckIcon } from 'lucide-react';
export function Features() {
  const features = [{
    title: 'AI Call Summaries',
    description: 'Generated automatically after every call. No rep action needed, so notes never get lost between back-to-back meetings.',
    icon: FileTextIcon,
    featured: true
  }, {
    title: 'Qualification Extraction',
    description: 'Runs during the conversation. By the time the rep hangs up, BANT or MEDDICC data is structured and ready.',
    icon: TargetIcon,
    featured: false
  }, {
    title: 'HubSpot Sync',
    description: 'Data flows into your CRM automatically. Reps never have to open a HubSpot tab to log a call.',
    icon: RefreshCwIcon,
    featured: false
  }, {
    title: 'Human-in-the-Loop',
    description: "An optional safety net. Admins can require a quick Slack approval before sync, but it's never mandatory.",
    icon: UserCheckIcon,
    featured: false
  }];
  const featuredItem = features[0];
  const restItems = features.slice(1);
  return <section id="features" className="py-28 md:py-40 bg-white border-y border-slate-100" data-id="element-35">
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12" data-id="element-36">
        <div className="text-center mb-20" data-id="element-37">
          <div className="text-xs uppercase tracking-[0.2em] font-medium text-teal-600 mb-4" data-id="element-38">
            Capabilities
          </div>
          <h2 className="text-4xl md:text-5xl font-medium text-slate-900 mb-6" data-id="element-39">
            Set it up once.
            <br className="hidden md:block" data-id="element-40" /> It handles the rest.
          </h2>
          <p className="text-base text-slate-600 font-normal max-w-xl mx-auto" data-id="element-41">
            Eliminate CRM busywork without changing how your team sells. Kinase
            runs silently in the background.
          </p>
        </div>

        {/* Featured card — full width, horizontal layout */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="bg-slate-50 border border-slate-200 rounded-xl p-10 lg:p-14 mb-6 flex flex-col lg:flex-row lg:items-center lg:gap-16" data-id="element-42">
          <div className="flex-1" data-id="element-43">
            <div className="w-14 h-14 rounded-xl bg-teal-50 flex items-center justify-center mb-6 border border-teal-200" data-id="element-44">
              <featuredItem.icon className="w-7 h-7 text-teal-600" data-id="element-45" />
            </div>
            <h3 className="text-2xl md:text-3xl font-medium text-slate-900 mb-4" data-id="element-46">
              {featuredItem.title}
            </h3>
            <p className="text-slate-600 font-normal text-lg leading-relaxed max-w-lg" data-id="element-47">
              {featuredItem.description}
            </p>
          </div>
          <div className="flex-shrink-0 mt-8 lg:mt-0 w-full lg:w-auto" data-id="element-48">
            {/* Realistic UI Preview based on the Kinase screenshot */}
            <div className="w-full lg:w-[380px] bg-white border border-slate-200 rounded-xl shadow-md overflow-hidden flex flex-col" data-id="element-49">
              <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2 bg-slate-50/80" data-id="element-50">
                <FileTextIcon className="w-4 h-4 text-teal-600" data-id="element-51" />
                <span className="text-sm font-medium text-slate-800" data-id="element-52">
                  Executive Summary
                </span>
              </div>
              <div className="p-5 space-y-4" data-id="element-53">
                <div className="flex gap-3 items-start" data-id="element-54">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-2 flex-shrink-0" data-id="element-55" />
                  <div className="text-xs text-slate-600 leading-relaxed" data-id="element-56">
                    Sarah met with David Park (VP Engineering) and Lisa Chen
                    (Head of Procurement) at Vantage Systems to discuss platform
                    migration timeline and budget.
                  </div>
                </div>
                <div className="flex gap-3 items-start" data-id="element-57">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-2 flex-shrink-0" data-id="element-58" />
                  <div className="text-xs text-slate-600 leading-relaxed" data-id="element-59">
                    Confirmed $75k budget approved for Q3. Current vendor
                    contract expires August 15, creating urgency to finalize by
                    end of July.
                  </div>
                </div>
                <div className="flex gap-3 items-start" data-id="element-60">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-2 flex-shrink-0" data-id="element-61" />
                  <div className="text-xs text-slate-600 leading-relaxed" data-id="element-62">
                    Key blocker: security review required before procurement can
                    proceed. David offered to fast-track if the security
                    questionnaire is completed this week.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Remaining 3 cards in a row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-63">
          {restItems.map((feature, index) => <motion.div key={feature.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.05,
          duration: 0.5
        }} className="bg-white border border-slate-200 shadow-sm rounded-xl p-8" data-id="element-64">
              <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center mb-5 border border-teal-200" data-id="element-65">
                <feature.icon className="w-5 h-5 text-teal-600" data-id="element-66" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 mb-2" data-id="element-67">
                {feature.title}
              </h3>
              <p className="text-slate-600 font-normal text-sm leading-relaxed" data-id="element-68">
                {feature.description}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}